import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import { ArrowRight, CheckCircle, Circle } from 'lucide-react'
import Banner from '../../Components/Banner';
import { Helmet } from 'react-helmet-async';

const featuresData = [
    {
        id: 1,
        title: "Advisory & Assessment",
        description:
            "Understand your EPM maturity, transform finance, and clearly define ROI.",
        image: "/assets/v/item6.png",
    },
    {
        id: 2,
        title: "Implementation & Rollout",
        description:
            "Complete Infor EPM setup, customized modules, smooth data migration, and easy system integration.",
        image: "/assets/v/item7.jpg",
    },
    {
        id: 3,
        title: "Optimization & Enhancement",
        description:
            "At Mobikode, we fine-tune performance, improve models, add new features, automate processes, and deliver meaningful AI insights.",
        image: "/assets/v/item8.jpg",
    },
    {
        id: 4,
        title: "Managed Services & Support",
        description:
            "Reliable application support, smooth cycle management, and continuous improvements to keep everything running effortlessly.",
        image: "/assets/v/item9.png",
    },
];

const toolsData = [
    {
        id: 1,
        title: "Infor-Focused Expertise",
        desc: "Hands-on experience delivering real value with Infor EPM and M3.",
    },
    {
        id: 2,
        title: "Finance + Technology DNA",
        desc: "We understand CFO priorities, not just systems.",
    },
    {
        id: 3,
        title: "Enterprise-Grade Delivery",
        desc: "Built-in security, governance, and scalability you can trust.",
    },
    {
        id: 4,
        title: "Automation-First Approach",
        desc: "Close deals faster with fewer mistakes and smoother workflows.",
    },
    {
        id: 5,
        title: "Global Delivery Model",
        desc: "Cost-effective, easy to scale, and built for reliable everyday use.",
    },
];

const whyMobikodePoints1 = [
    "Smart financial planning for better business clarity",
    "Accurate budgeting and forecasting for steady growth",
    "Scenario and “what-if” analysis for informed decisions",
    "Driver-based planning to understand key business factors",
];
const whyMobikodePoints2 = [
    "Simplified consolidation across multiple entities and currencies",
    "Clear statutory and management reporting for better decisions",
    "Smooth intercompany elimination for accurate accounts",
    "Fast, audit-ready financial close with full confidence",
];
const whyMobikodePoints3 = [
    "Make complex data simple and easy to understand",
    "KPI frameworks and easy-to-track performance dashboards",
    "Clear and insightful management & board reporting",
    "Detailed profitability tracking and cost analysis",
    "Reliable regulatory and compliance reporting for business assurance",
];
const whyMobikodePoints4 = [
    "Real-time integration with Infor M3 for seamless data flow",
    "Harmonized chart of accounts for consistent financial structure",
    "Automated data validation and reconciliation to reduce manual effort",
    "A single source of truth for accurate and reliable enterprise reporting",
];

const whyMobikodePoints5 = [
    "Manufacturing and industrial businesses",
    "Supply chain and distribution teams",
    "Mid-to-large enterprises using Infor",
    "Finance and transformation leaders",
];


function InforEPMServices() {
    return (
        <div>
            <Helmet>
    <title>
        Infor EPM Services | Consulting, Implementation & Support | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode provides Infor EPM services for enterprise planning, forecasting, financial consolidation, performance management, reporting, implementation, optimization, integration, and managed support."
    />

    <meta
        name="keywords"
        content="Infor EPM Services, Infor EPM Consulting, Infor EPM Implementation, Infor EPM Solutions, Infor EPM Support Services, Infor EPM Managed Services, Infor EPM Optimization, Infor EPM Planning, Infor EPM Forecasting, Infor EPM Financial Consolidation, Infor EPM Reporting, Infor EPM Performance Management, Infor EPM Integration, Infor EPM M3 Integration, Infor EPM Consultant, Enterprise Performance Management Services"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/infor-epm-services"
    />

    <meta
        property="og:title"
        content="Infor EPM Services | Consulting, Implementation & Support | Mobikode"
    />

    <meta
        property="og:description"
        content="Transform enterprise planning and financial performance with Mobikode's Infor EPM services, covering implementation, forecasting, consolidation, reporting, optimization, integration, and ongoing support."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/infor-epm-services"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/service-detail/img17.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Infor EPM Services | Consulting, Implementation & Support | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Explore Mobikode's Infor EPM expertise for enterprise planning, forecasting, financial consolidation, performance reporting, Infor M3 integration, optimization, and managed services."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/service-detail/img17.png"
    />
</Helmet>
            <AiHero title="Infor EPM Services" imgData="/assets/service-detail/img17.png" />
            <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px]">
                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/v/item12.png"
                                alt="ai automation"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">
                            <h3 className="text-[#01386A] text-[20px] lg:text-[30px] font-[600] font-sora mb-[15px] lg:mb-[35px] leading-[1.3]">

                                Smarter Planning. Faster Decisions. Enterprise-Ready Performance.

                            </h3>
                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                At Mobikode, we help businesses get real value from Infor EPM by turning complex data into simple, useful insights. From setup to ongoing improvements, we make sure your planning and forecasting stay connected to your goals so you can make better decisions, faster and with confidence.</p>
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>
                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>
                        </div>
                    </div>
                </div>
            </section>
            <WhyAIAutomation title="Service Offerings" fData={featuresData} />
            {/* <TechServicesSection title="Explore Our Services" sData={services} /> */}
            {/* <IndustriesWeServe /> */}

            <section className="w-full bg-white pb-16 lg:pb-24 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="grid lg:grid-cols-2 grid-cols-1 gap-6 lg:gap-10">

                        <div className="bg-white p-8 lg:p-12 rounded-none border border-gray-100 border-t-4 border-t-[#01386A] transition-all duration-300 group hover:border-[#F06115] hover:border-t-[#F06115] hover:shadow-[0_20px_50px_rgb(240,97,21,0.12)]">
                            <div className="text-left mb-10 pt-2">
                                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4 leading-tight">
                                    Enterprise Planning & Forecasting
                                </h2>
                                <p className="text-[#49577A] font-graphik font-[500] text-[14px] lg:text-[16px]">
                                    Help your business plan smarter and adapt quickly across finance, operations, workforce, and capital, so decisions become simpler, faster, and more effective.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-y-3 relative z-10">
                                {whyMobikodePoints1.map((point, index) => (
                                    <div key={index} className="flex items-start gap-4 p-3 rounded-none group/item hover:bg-[#F06115]/5 transition-all duration-300 cursor-pointer">
                                        <div className="mt-1 shrink-0">
                                            <Circle size={14} className="text-[#F06115] fill-transparent group-hover/item:fill-[#F06115]/30 transition-all duration-300 shrink-0" />
                                        </div>
                                        <p className="text-[#49577A] font-graphik lg:text-[16px] text-[14px] font-[500] group-hover/item:text-[#01386A] transition-colors duration-300">
                                            {point}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-8 lg:p-12 rounded-none border border-gray-100 border-t-4 border-t-[#01386A] transition-all duration-300 group hover:border-[#F06115] hover:border-t-[#F06115] hover:shadow-[0_20px_50px_rgb(240,97,21,0.12)]">
                            <div className="text-left mb-10 pt-2">
                                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4 leading-tight">
                                    Financial Consolidation & Close
                                </h2>
                                <p className="text-[#49577A] font-graphik font-[500] lg:text-[16px] text-[14px]">
                                    Make your financial close faster and easier with automation that improves accuracy and keeps everything compliant without the usual stress or delays.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-y-3 relative z-10">
                                {whyMobikodePoints2.map((point, index) => (
                                    <div key={index} className="flex items-start gap-4 p-3 rounded-none group/item hover:bg-[#F06115]/5 transition-all duration-300 cursor-pointer">
                                        <div className="mt-1 shrink-0">
                                            <Circle size={14} className="text-[#F06115] fill-transparent group-hover/item:fill-[#F06115]/30 transition-all duration-300 shrink-0" />
                                        </div>
                                        <p className="text-[#49577A] font-graphik lg:text-[16px] text-[14px] font-[500] group-hover/item:text-[#01386A] transition-colors duration-300">
                                            {point}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-8 lg:p-12 rounded-none border border-gray-100 border-t-4 border-t-[#01386A] transition-all duration-300 group hover:border-[#F06115] hover:border-t-[#F06115] hover:shadow-[0_20px_50px_rgb(240,97,21,0.12)]">
                            <div className="text-left mb-10 pt-2">
                                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4 leading-tight">
                                    Performance Management & Reporting
                                </h2>
                                <p className="text-[#49577A] font-graphik font-[500] lg:text-[16px] text-[14px]">
                                    Turn complex data into executive-ready insights.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-y-3 relative z-10">
                                {whyMobikodePoints3.map((point, index) => (
                                    <div key={index} className="flex items-start gap-4 p-3 rounded-none group/item hover:bg-[#F06115]/5 transition-all duration-300 cursor-pointer">
                                        <div className="mt-1 shrink-0">
                                            <Circle size={14} className="text-[#F06115] fill-transparent group-hover/item:fill-[#F06115]/30 transition-all duration-300 shrink-0" />
                                        </div>
                                        <p className="text-[#49577A] font-graphik lg:text-[16px] text-[14px] font-[500] group-hover/item:text-[#01386A] transition-colors duration-300">
                                            {point}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-8 lg:p-12 rounded-none border border-gray-100 border-t-4 border-t-[#01386A] transition-all duration-300 group hover:border-[#F06115] hover:border-t-[#F06115] hover:shadow-[0_20px_50px_rgb(240,97,21,0.12)]">
                            <div className="text-left mb-10 pt-2">
                                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4 leading-tight">
                                    Infor EPM + Infor M3 Integration
                                </h2>
                                <p className="text-[#49577A] font-graphik font-[500] lg:text-[16px] text-[14px]">
                                    Connect operations and finance for simpler, smarter decision-making.

                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-y-3 relative z-10">
                                {whyMobikodePoints4.map((point, index) => (
                                    <div key={index} className="flex items-start gap-4 p-3 rounded-none group/item hover:bg-[#F06115]/5 transition-all duration-300 cursor-pointer">
                                        <div className="mt-1 shrink-0">
                                            <Circle size={14} className="text-[#F06115] fill-transparent group-hover/item:fill-[#F06115]/30 transition-all duration-300 shrink-0" />
                                        </div>
                                        <p className="text-[#49577A] font-graphik font-[500] lg:text-[16px] text-[14px] group-hover/item:text-[#01386A] transition-colors duration-300">
                                            {point}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </section>
            <section className="w-full bg-[#FDFDFD] pb-16 lg:pb-24 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="mb-[30px] lg:mb-[60px]">
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                            Why Enterprises Choose Mobikode
                        </h2>
                    </div>

                    {/* Grid for Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {toolsData.map((item) => (
                            <div
                                key={item.id}
                                className="relative bg-[#F0F0F0] p-8 min-h-[180px] flex flex-col justify-center overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2 leading-tight">
                                        {item.title}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                                        {item.desc}
                                    </p>
                                </div>
                                <div
                                    className="absolute bottom-0 right-0 w-[180px] h-[140px] pointer-events-none transition-transform duration-500 group-hover:scale-110 opacity-50 lg:opacity-100"
                                >
                                    <img src="/assets/career/wrap1.svg" alt="" className="w-full h-full object-contain object-bottom-right" />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>
            <section className="w-full bg-white pb-10 lg:pb-10 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="bg-[#F9F9F9] p-6 lg:p-12 border ">
                        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
                            <div className="w-full lg:w-3/5">
                                <div className="text-left mb-10">
                                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4">
                                        Who We Work With
                                    </h2>
                                    {/* Added a separator line for better design, optional */}
                                    <div className="w-20 h-1 bg-[#F06115] rounded"></div>
                                </div>

                                {/* Checkpoints Grid */}
                                <div className="grid grid-cols-1 gap-y-5">
                                    {whyMobikodePoints5.map((point, index) => (
                                        <div key={index} className="flex items-start gap-3 group">
                                            <div className="mt-1.5 flex-shrink-0">
                                                <Circle size={10} className="text-[#F06115] fill-[#F06115]/10" />
                                            </div>
                                            <p className="text-[#01386A] font-graphik text-[14px] lg:text-[16px] font-[500] leading-relaxed group-hover:text-[#F06115] transition-colors duration-300">
                                                {point}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* RIGHT SIDE: Image */}
                            <div className="w-full lg:w-2/5 flex justify-center items-center">
                                <div className="relative w-full max-w-[400px] lg:max-w-none aspect-square">
                                    {/* Placeholder image - replace src with your actual image path */}
                                    <img
                                        src="/assets/what/img7.png"
                                        alt="Collaboration concept"
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Optional decorative element to match the style */}
                                    <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#F06115]/10 rounded-full -z-10"></div>
                                    <div className="absolute -top-4 -right-4 w-12 h-12 bg-[#01386A]/5 rounded-full -z-10"></div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
            {/* <section className="relative w-full h-[400px] lg:h-[507px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/career/img12.png"
                        alt="Agentic AI Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] h-full flex items-center lg:items-center ">
                    <div className="bg-white p-8 lg:p-12 max-w-[588px] shadow-2xl rounded-sm">

                        <h2 className="text-[#01386A] text-[30px] lg:text-[40px] font-[600] font-sora leading-tight mb-6">
                            Agentic AI Services for Enterprises
                        </h2>
                        <p className="text-[#49577A] text-[16px] leading-[24px] font-graphik mb-8">
                            Helping businesses grow with smart, reliable AI that simplifies work, boosts efficiency, and sparks innovation.

                        </p>
                        <a href="/contact" className="inline-flex items-center gap-2 text-[#01386A] underline font-graphik font-semibold text-lg hover:gap-3 transition-all duration-300 group">
                            Contact Now
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </a>

                    </div>

                </div>

            </section> */}
            <Banner title="Infor EPM Services" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
        </div>
    )
}

export default InforEPMServices
